/**
 * Depot des photos de la prise.
 *
 * Un bloc peut contenir plusieurs zones (1, 3 ou 10 selon l'offre) : chaque
 * zone est autonome — sa vignette, son poids, son cadrage.
 *
 * LE HEIC EST CONVERTI ICI, avant l'envoi. Longtemps on l'a laisse passer tel
 * quel en comptant sur l'atelier pour le convertir a la reception : c'etait
 * exact mais insuffisant, parce que le client, lui, ne voyait rien — ni sa
 * vignette, ni sa carte dans l'apercu en direct, ni sa photo dans le panier.
 * Un JPEG part donc toujours vers Shopify, quel que soit le fichier choisi.
 *
 * En JS natif, sans dependance aux classes internes de Horizon : ce bloc doit
 * survivre a une mise a jour du theme.
 */
const MO = 1024 * 1024;

// Au-dela, on ne tente meme pas le decodage : le navigateur y passerait une
// minute pour finir par manquer de memoire. Un HEIC de telephone fait 2 a 5 Mo.
const HEIC_ABSURDE = 80 * MO;

const estHeic = (f) => /^image\/hei[cf]/i.test(f.type) || /\.(heic|heif)$/i.test(f.name);

// Chrome et Safari n'ont pas eu le meme defaut pour imageOrientation, et une
// valeur inconnue leve : on tente la forme explicite, on retombe sur le defaut.
async function versBitmap(source) {
  try {
    return await createImageBitmap(source, { imageOrientation: 'from-image' });
  } catch {
    return await createImageBitmap(source);
  }
}

// heic2any pese 1,3 Mo : il n'est telecharge que le jour ou un client depose
// un HEIC depuis un navigateur qui ne sait pas le lire, et une seule fois.
let decodeurCharge = null;
function chargerDecodeur(url) {
  if (window.heic2any) return Promise.resolve(window.heic2any);
  if (!url) return Promise.reject(new Error('URL du decodeur absente'));
  if (decodeurCharge) return decodeurCharge;

  decodeurCharge = new Promise((ok, ko) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = () =>
      window.heic2any ? ok(window.heic2any) : ko(new Error('heic2any charge mais absent'));
    script.onerror = () => ko(new Error('heic2any injoignable'));
    document.head.appendChild(script);
  }).catch((e) => {
    decodeurCharge = null; // un echec reseau ne doit pas condamner les essais suivants
    throw e;
  });
  return decodeurCharge;
}

// Toujours dans cet ordre : Safari et iOS lisent le HEIC nativement, et c'est
// 1,3 Mo de telechargement en moins pour la moitie des clients.
async function decoderHeic(fichier, urlDecodeur) {
  try {
    return await versBitmap(fichier);
  } catch {
    /* Chrome, Firefox : ils ne savent pas. On sort le decodeur embarque. */
  }
  const heic2any = await chargerDecodeur(urlDecodeur);
  const sortie = await heic2any({ blob: fichier, toType: 'image/jpeg', quality: 0.92 });
  // Un HEIC peut contenir plusieurs images (rafale, Live Photo) : on prend la premiere.
  return await versBitmap(Array.isArray(sortie) ? sortie[0] : sortie);
}

// Le JPEG produit est borne au meme cote que scripts/ingest_photo.mjs : convertir
// ici ne retire donc rien a la carte livree, et divise le poids de l'envoi.
async function bitmapVersJpeg(bitmap, nom, maxCote) {
  const facteur = Math.min(1, maxCote / Math.max(bitmap.width, bitmap.height));
  const largeur = Math.max(1, Math.round(bitmap.width * facteur));
  const hauteur = Math.max(1, Math.round(bitmap.height * facteur));

  const toile = document.createElement('canvas');
  toile.width = largeur;
  toile.height = hauteur;
  toile.getContext('2d').drawImage(bitmap, 0, 0, largeur, hauteur);
  bitmap.close?.();

  const blob = await new Promise((r) => toile.toBlob(r, 'image/jpeg', 0.9));
  if (!blob) throw new Error('conversion canvas vide');

  return new File([blob], `${nom.replace(/\.(heic|heif)$/i, '')}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

class Zone {
  constructor(racine, parent) {
    this.parent = parent;
    this.input = racine.querySelector('[ref="input"]');
    this.apercu = racine.querySelector('[ref="apercu"]');
    this.vignette = racine.querySelector('[ref="vignette"]');
    this.recue = racine.querySelector('[ref="recue"]');
    this.etat = racine.querySelector('[ref="etat"]');
    this.nom = racine.querySelector('[ref="nom"]');
    this.poids = racine.querySelector('[ref="poids"]');
    this.retirer = racine.querySelector('[ref="retirer"]');
    this.cadrage = racine.querySelector('[ref="cadrage"]');
    this.zone = racine.querySelector('.__zone');
    this.url = null;
    this.tour = 0; // un depot en chasse un autre : la conversion en cours devient caduque

    this.input?.addEventListener('change', () => this.traiter(this.input.files?.[0]));
    this.retirer?.addEventListener('click', () => this.vider());

    for (const e of ['dragenter', 'dragover']) {
      this.zone?.addEventListener(e, (ev) => { ev.preventDefault(); this.zone.classList.add('is-survol'); });
    }
    for (const e of ['dragleave', 'drop']) {
      this.zone?.addEventListener(e, (ev) => { ev.preventDefault(); this.zone.classList.remove('is-survol'); });
    }
    this.zone?.addEventListener('drop', (ev) => {
      const f = ev.dataTransfer?.files?.[0];
      if (!f) return;
      this.poser(f);
      this.traiter(f);
    });
  }

  // Ce que le navigateur enverra reellement dans le formulaire.
  poser(fichier) {
    const dt = new DataTransfer();
    dt.items.add(fichier);
    this.input.files = dt.files;
  }

  patienter(message) {
    if (!this.etat) return;
    this.etat.textContent = message ?? '';
    this.etat.hidden = !message;
  }

  async traiter(fichier) {
    const tour = ++this.tour;
    this.parent.erreur(null);
    this.patienter(null);
    if (!fichier) return this.vider();

    if (estHeic(fichier)) {
      if (fichier.size > HEIC_ABSURDE) {
        this.vider();
        return this.parent.erreur(
          `Cette photo pèse ${(fichier.size / MO).toFixed(0)} Mo : c'est trop lourd pour être converti ` +
            `dans le navigateur. Envoyez-la en JPEG.`
        );
      }
      this.patienter(`Conversion de ${fichier.name}…`);
      try {
        const bitmap = await decoderHeic(fichier, this.parent.urlDecodeur);
        fichier = await bitmapVersJpeg(bitmap, fichier.name, this.parent.maxCote);
      } catch {
        if (tour !== this.tour) return; // le client a depose autre chose entre-temps
        this.patienter(null);
        this.vider();
        return this.parent.erreur(
          `Ce fichier HEIC n'a pas pu être converti par votre navigateur. ` +
            `Sur iPhone : Réglages → Appareil photo → Formats → « Plus compatible », ` +
            `puis reprenez la photo — ou envoyez-la en JPEG.`
        );
      }
      if (tour !== this.tour) return;
      this.patienter(null);
      this.poser(fichier); // desormais, seul ce JPEG partira chez Shopify
    }

    // Le poids se mesure sur le fichier REELLEMENT envoye, pas sur l'original :
    // un HEIC de 6 Mo devient un JPEG de 1 Mo, il n'y a plus lieu de le refuser.
    if (fichier.size > this.parent.maxOctets) {
      const mo = (fichier.size / MO).toFixed(1);
      this.vider();
      return this.parent.erreur(
        `Cette photo pèse ${mo} Mo, la limite est de ${this.parent.maxMo} Mo. ` +
          `Sur iPhone : Réglages → Appareil photo → Formats → « Haute efficacité ».`
      );
    }

    this.nom.textContent = fichier.name;
    this.poids.textContent = `${(fichier.size / MO).toFixed(1)} Mo`;

    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(fichier);

    // On PRE-REMPLIT le cadrage d'apres l'orientation reelle, sans l'imposer :
    // une photo verticale peut tres bien meriter un cadre paysage.
    // Une NOUVELLE photo rouvre la suggestion : le choix fait a la main valait
    // pour la precedente. Sans cela, un client qui avait coche Paysage pour une
    // premiere photo gardait Paysage sur une photo verticale — et la carte
    // coupait la moitie de son poisson.
    if (this.cadrage) delete this.cadrage.dataset.choisi;
    const sonde = new Image();
    sonde.onload = () => {
      this.suggererCadrage(
        sonde.naturalWidth > sonde.naturalHeight ? 'Paysage'
          : sonde.naturalWidth === sonde.naturalHeight ? 'Carré' : 'Portrait'
      );
      this.montrerVignette(true);
      this.parent.prevenir();
    };
    // Filet de securite : depuis la conversion, plus aucun format indecodable
    // ne devrait arriver ici. Mieux vaut une pastille « recue » qu'un carre vide.
    sonde.onerror = () => {
      this.montrerVignette(false);
      this.poids.textContent += ' · photo bien reçue, aperçu impossible dans ce navigateur';
      this.parent.prevenir();
    };
    sonde.src = this.url;

    this.vignette.src = this.url;
    this.apercu.hidden = false;
    if (this.cadrage) this.cadrage.hidden = false;
    this.parent.prevenir();
  }

  // Ne bouscule pas un choix deja fait a la main par le client.
  suggererCadrage(valeur) {
    if (!this.cadrage || this.cadrage.dataset.choisi) return;
    const r = this.cadrage.querySelector(`input[value="${valeur}"]`);
    if (r) r.checked = true;
  }

  montrerVignette(ok) {
    this.vignette.hidden = !ok;
    if (this.recue) this.recue.hidden = ok;
    if (!ok) this.vignette.removeAttribute('src');
  }

  vider() {
    this.tour++; // annule une conversion encore en vol
    this.input.value = '';
    this.patienter(null);
    this.apercu.hidden = true;
    if (this.cadrage) this.cadrage.hidden = true;
    if (this.url) { URL.revokeObjectURL(this.url); this.url = null; }
    this.montrerVignette(true);
    this.vignette.removeAttribute('src');
    this.parent.prevenir();
  }
}

class PhotosPrise extends HTMLElement {
  connectedCallback() {
    if (this.dataset.pret) return;
    this.dataset.pret = '1';

    const reglages = this.querySelector('[data-max-mo]');
    this.maxMo = Number(reglages?.dataset.maxMo ?? this.dataset.maxMo) || 20;
    this.maxOctets = this.maxMo * MO;
    this.maxCote = Number(reglages?.dataset.maxCote) || 2400;
    this.urlDecodeur = reglages?.dataset.heicUrl ?? null;
    this.boiteErreur = this.querySelector('[ref="erreur"]');

    this.zones = [...this.querySelectorAll('.__groupe')].map((g) => new Zone(g, this));

    // Un clic sur le cadrage vaut choix explicite : on cesse de suggerer.
    this.addEventListener('change', (e) => {
      const f = e.target.closest?.('[ref="cadrage"]');
      if (f) { f.dataset.choisi = '1'; this.prevenir(); }
    });
  }

  // L'apercu de la carte ecoute cet evenement pour se recomposer.
  prevenir() {
    this.dispatchEvent(new CustomEvent('photos:maj', {
      bubbles: true,
      detail: { photos: this.zones.map((z) => z.url) },
    }));
  }

  erreur(message) {
    if (!this.boiteErreur) return;
    this.boiteErreur.textContent = message ?? '';
    this.boiteErreur.hidden = !message;
  }
}

if (!customElements.get('product-photo-component')) {
  customElements.define('product-photo-component', PhotosPrise);
}
